/**
 * Verbatim pools per KPI and sentiment, conditioned on the selected launch
 * options and the audience. Quotes are written the way a real open-end reads:
 * a usage moment, a comparison point, and a reason.
 */

import { AUDIENCES, FACTORS } from "./model";
import { AudienceId, Configuration, KpiId, Sentiment } from "./types";

/* ── Public API ─────────────────────────────────── */

export const quotePools = (
  kpi: KpiId,
  c: Configuration,
  audience: AudienceId
): Record<Sentiment, string[]> => {
  const price = label(c, "price");
  const size = label(c, "packSize");
  const claim = label(c, "claim");
  const retail = retailer(c);
  const audienceLabel =
    AUDIENCES.find((a) => a.id === audience)?.label ?? "consumers";
  const isParent = audience === "parents";
  const isNutellaFan = audience === "nutellaBuyers";
  const isPbHeavy = audience === "pbHeavy";
  const isGenZ = audience === "genz";
  const isMillennial = audience === "millennials";
  const expensive = c.price === "5.99" || c.price === "6.49";
  const cheap = c.price === "4.99";
  const healthAngle =
    c.positioning === "protein" || c.positioning === "natural";
  const peanutLook =
    c.packaging === "peanutLed" || c.packaging === "peanutOnly";

  const positive: string[] = [];
  const neutral: string[] = [];
  const negative: string[] = [];
  const add = (list: string[], when: boolean, ...quotes: string[]) => {
    if (when) list.push(...quotes);
  };

  switch (kpi) {
    case "purchaseIntent":
      add(
        positive,
        true,
        "Nutella and peanut butter in one jar is exactly what I've been making at home on Sunday mornings. I'd buy it the first time I see it, no question.",
        `${price} is fair for a Nutella product. Regular Nutella is already around $5 at my store, so paying a bit more for something new feels normal.`,
        `The ${size} jar is the right size for us, big enough to last a couple of weeks of toast without going stale.`,
        "It's the one new spread I'd actually switch for. Most 'new flavors' are gimmicks, but salty-sweet peanut and cocoa is a real combination."
      );
      add(
        positive,
        isParent,
        "My kids fight over Nutella and over peanut butter. This ends the argument. It would go straight into the lunchbox rotation."
      );
      add(
        positive,
        isNutellaFan,
        "I've bought every Nutella launch, including the biscuits and the B-ready bars. This is the first one that feels like a new everyday spread rather than a snack."
      );
      add(
        positive,
        isPbHeavy,
        "I go through a jar of peanut butter a week. If this tastes like real peanut butter with a cocoa layer, it replaces one of those jars easily."
      );
      add(
        positive,
        isGenZ,
        "This is the kind of thing I'd see once on TikTok and buy the same week. It's fun, it's Nutella, and it's not trying too hard."
      );
      add(
        positive,
        isMillennial,
        "We already buy both for the house. One jar instead of two saves fridge space and, honestly, I'm curious."
      );
      add(
        positive,
        c.claim === "realPeanuts",
        `"${claim}" on the label matters to me. I want to know it's actual peanuts and not peanut flavoring.`
      );
      add(
        positive,
        c.positioning === "indulgence",
        "I don't want a healthier Nutella, I want a more indulgent one. Positioning it as a treat is exactly right."
      );
      add(
        neutral,
        true,
        "Sounds nice, but I'd want to taste it before committing. Some of these combos are much sweeter than you expect.",
        `I'd try it once, probably on promo, but at ${price} I'm not sure it becomes a regular buy for us.`,
        "It depends how much it tastes like peanut butter versus Nutella. If it's just sweeter Nutella I'll pass.",
        "I'd buy it for a special occasion or for pancakes at the weekend, but not as my everyday spread."
      );
      add(
        negative,
        true,
        "Too sweet for everyday use. I'd stick with my regular peanut butter and keep Nutella for the weekend.",
        "We already have both jars in the cupboard. I don't see what a third one adds except another thing to finish.",
        "Peanut butter is a savoury thing in my house, on celery, in sauces. Mixing it with chocolate spread isn't for me.",
        "I've tried a chocolate-peanut spread before and it tasted like neither. I'd need someone to convince me this one is different.",
        "My partner is allergic to peanuts, so a Nutella jar with peanuts is actually a jar I have to keep out of the house."
      );
      add(
        negative,
        expensive,
        `${price} is steep for peanut butter. My usual jar is under $4, so this would need to be a very occasional treat.`,
        "That's almost double what I pay for peanut butter. I'd rather buy a jar of each and mix them myself."
      );
      add(
        negative,
        peanutLook,
        "I honestly almost didn't realise it was Nutella. The jar looks like any store-brand peanut butter, so I'd walk past it."
      );
      add(
        negative,
        c.positioning === "protein" && isNutellaFan,
        "Nutella isn't a protein brand to me. Reading 'protein' on a Nutella jar makes me think they changed the recipe."
      );
      add(
        negative,
        c.positioning === "indulgence" && isPbHeavy,
        "I buy peanut butter as a healthy staple, not as a dessert. Framing it as an indulgence tells me it's more sugar than peanuts."
      );
      add(
        negative,
        c.packaging === "peanutOnly",
        "If it's really made by Nutella, why hide the logo? It makes me wonder whether it's the real thing or a licensed knock-off."
      );
      add(
        negative,
        healthAngle,
        "Nutella talking about health is a stretch. I'd rather they own the indulgence than pretend it's a wellness product."
      );
      add(
        negative,
        true,
        "I don't see when I'd use it. It's not quite a spread and not quite a dessert. It would sit in the cupboard."
      );
      break;

    case "wtp":
      add(
        positive,
        true,
        `${price} is what I'd expect for a branded jar this size. Nutella has never been cheap and people still buy it every week.`,
        "I'd pay a premium over regular peanut butter because there's actual Nutella in it, not just a chocolate flavor.",
        `Around ${price} feels right. It's a treat, so I don't compare it with the $3 peanut butter on the bottom shelf.`
      );
      add(
        positive,
        isNutellaFan,
        "I already pay $5 or more for the big Nutella jar. Another dollar for something new and limited doesn't bother me."
      );
      add(
        positive,
        c.packSize === "20",
        `For a ${size} jar that's actually good value per ounce compared with what I pay for Nutella today.`
      );
      add(
        neutral,
        true,
        "I'd pay about the same as a premium peanut butter, so around $5. Above that I'd want to taste it first.",
        `${price} is my ceiling. I'd buy it at that price now and then, but it would be the first thing I'd drop if money got tight.`,
        `It depends on the size. ${size} for ${price} is borderline. A bigger jar at the same price and I'm in.`
      );
      add(
        negative,
        true,
        "Peanut butter is $3 at my store. I'd pay $4.50 tops for this, and only because of the Nutella name.",
        "For that price I'd rather buy a jar of each and swirl them myself. It's not hard."
      );
      add(
        negative,
        expensive,
        `${price} feels like a launch price they'll discount later. I'd wait for the coupon.`
      );
      add(
        negative,
        c.retail === "walmart" || c.retail === "mass",
        `At ${retail} I expect value pricing. This feels too premium for that aisle and I'd assume it's overpriced.`
      );
      add(
        negative,
        cheap && isNutellaFan,
        `${price} is almost suspiciously low for Nutella. It makes me wonder whether there's much Nutella in it at all.`
      );
      break;

    case "appeal":
      add(
        positive,
        true,
        "The idea is genuinely tempting: creamy, chocolatey, a bit salty. It's the flavor combination everyone already loves in candy bars.",
        "It's a real new idea in a boring category. The spreads aisle has looked the same for twenty years.",
        `"${claim}" is exactly what I'd look for on the label. It tells me what it is without overselling.`
      );
      add(
        positive,
        c.packaging === "balanced",
        "The jar shows both, the Nutella swirl and the peanuts. You understand it in two seconds on the shelf."
      );
      add(
        positive,
        c.packaging === "nutellaLed",
        "It looks like the Nutella I know with a peanut twist. That's reassuring; I know what I'm getting."
      );
      add(
        positive,
        isGenZ,
        "It looks fun and very shareable. I can already picture the toast pictures."
      );
      add(
        positive,
        isParent,
        "Anything that gets my kids to eat breakfast without a fight is appealing. This would do it."
      );
      add(
        neutral,
        true,
        "Interesting, but I'm wondering whether it's just Nutella with peanut flavoring. The concept is good; the taste will decide.",
        "I like the idea more than I think I'd like the product. Two strong flavors can cancel each other out."
      );
      add(
        negative,
        true,
        "Feels like a gimmick. Two good things don't always make a better one. It reminds me of the flavor mash-ups that disappear after a year.",
        "Too indulgent for me. I'd feel guilty putting it on toast every day, so I'm not sure where it fits."
      );
      add(
        negative,
        c.claim === "sourceOfProtein",
        `Calling a Nutella product a "source of protein" feels like a stretch. It makes the whole thing less believable.`
      );
      add(
        negative,
        peanutLook,
        "The jar is bland. If you're going to put Nutella in it, show me the Nutella."
      );
      break;

    case "brandFit":
      add(
        positive,
        true,
        "Totally makes sense for Nutella. Peanuts and cocoa go together, so it's the obvious next flavor rather than a random extension.",
        "It still feels like Nutella, just with a twist. That's how a brand should stretch: not into cereal or drinks, but next door.",
        "Nutella plus nuts is on brand. It's the same promise: a rich, sweet spread the whole family argues over."
      );
      add(
        positive,
        c.packaging === "nutellaOnly",
        "The Nutella branding front and center tells me exactly what to expect. I trust the taste before I open it."
      );
      add(
        positive,
        c.positioning === "indulgence",
        "Keeping it as a treat is the right call. That's what Nutella is; it shouldn't apologise for it."
      );
      add(
        neutral,
        true,
        "It's fine, but Nutella is chocolate-hazelnut to me. Peanut is a small step away and I'm not sure it needed taking.",
        "I can't tell whether this is for Nutella fans or for peanut butter fans. Maybe both, maybe neither."
      );
      add(
        negative,
        peanutLook,
        "This looks like a generic peanut butter that borrowed the logo. It doesn't carry any of the Nutella feeling."
      );
      add(
        negative,
        healthAngle,
        "Nutella talking about protein or 'natural' is a bit rich given the sugar. Stick to what the brand is good at."
      );
      add(
        negative,
        c.packaging === "peanutOnly",
        "If Nutella is barely on the jar, why would I trust that it's Nutella inside? Either own it or don't use the name."
      );
      add(
        negative,
        isNutellaFan,
        "Feels like it dilutes what Nutella stands for. I want the original done well, not a line of variations."
      );
      add(
        negative,
        true,
        "It feels like a marketing idea rather than a product idea. Nutella doesn't need a peanut version."
      );
      break;

    case "awareness":
      add(
        positive,
        true,
        `I shop at ${retail} every week and always pass the spreads aisle. A new Nutella jar with a different color would catch my eye.`,
        "A Nutella launch always gets talked about. My sister sends me every new thing they do."
      );
      add(
        positive,
        c.advertising === "social" ||
          c.advertising === "influencer" ||
          c.advertising === "socialRetailMedia",
        "If it shows up in my feed once with someone spreading it on toast, I'd remember it next time I'm in the store."
      );
      add(
        positive,
        c.advertising === "tv" || c.advertising === "tvSocial",
        "TV still works on me for food. Those Nutella breakfast ads are the ones I actually remember."
      );
      add(
        positive,
        c.retail === "walmartTarget" || c.retail === "mass",
        "If it's in both Walmart and Target it's hard to miss. That's where I do all my food shopping."
      );
      add(
        neutral,
        true,
        "I don't pay attention to new products unless someone recommends them. I'd probably hear about it eventually.",
        `Maybe, if it's on an end-cap at ${retail}. Buried in the aisle, I'd miss it.`
      );
      add(
        negative,
        c.retail === "wholeFoods" ||
          c.retail === "target" ||
          c.retail === "kroger",
        `I don't shop at ${retail}, so unless it's advertised I wouldn't know it exists.`
      );
      add(
        negative,
        c.advertising === "tv",
        "I don't watch live TV anymore. If the campaign is on TV, I'd miss the launch entirely."
      );
      add(
        negative,
        c.advertising === "retailMedia",
        "I never look at the screens in store or the ads on the app. Those don't register for me."
      );
      add(
        negative,
        true,
        "The spreads aisle is crowded and new jars blend in. Unless it's a display, I'd walk straight past."
      );
      break;

    case "trial":
      add(
        positive,
        true,
        "I'd try it the first time I see it. Nutella has earned that. I've never bought something from them I didn't finish.",
        `${price} is low enough risk to try once. Worst case my kids eat it.`
      );
      add(
        positive,
        isGenZ,
        "I'd buy it once just to post about it, honestly. Then we'd see if it's good enough to buy again."
      );
      add(
        positive,
        c.packSize === "12",
        `A ${size} jar is a nice trial size. I don't want to commit to a big jar of something I've never tasted.`
      );
      add(
        neutral,
        true,
        "I'd wait for a friend to try it first, or for a review. I don't buy new spreads on impulse.",
        "Maybe if there's a sample in store. I like to taste before I buy anything sweet."
      );
      add(
        negative,
        true,
        "I have my peanut butter and I don't switch easily. It took me years to settle on the one I buy now.",
        `${size} is a big commitment for something I might not like. Half of it would end up in the bin.`
      );
      add(
        negative,
        c.price === "6.49",
        `${price} is too much to gamble on a first try. I'd need to know I like it first.`
      );
      add(
        negative,
        isPbHeavy,
        "I eat peanut butter for the protein and the taste, not for chocolate. I don't see myself even trying it."
      );
      break;
  }

  // Ensure no pool is ever empty.
  if (neutral.length === 0)
    neutral.push(
      `Hard to say without tasting it. Like most ${audienceLabel.toLowerCase()} I'd need to see it in store first.`
    );
  if (negative.length === 0)
    negative.push(
      "Not for me. I'm happy with what I buy today and I don't need another sweet spread in the house."
    );
  return { negative, neutral, positive };
};

/* ── Private helpers ────────────────────────────── */

const label = (configuration: Configuration, factor: keyof Configuration) =>
  FACTORS.find((f) => f.id === factor)?.options.find(
    (o) => o.value === configuration[factor]
  )?.label ?? configuration[factor];

/** First retailer of the selected distribution, the one a shopper would name. */
const retailer = (configuration: Configuration): string => {
  const factor = FACTORS.find((f) => f.id === "retail");
  const option = factor?.options.find((o) => o.value === configuration.retail);
  const first = option?.members?.[0];
  return (
    factor?.members?.find((m) => m.id === first)?.label ??
    option?.label ??
    "the store"
  );
};
